import { supabase } from '../supabase';
import { User } from '../utils/types';

export interface AnalyticsData {
  tasksCount: number;
  taskersCount: number;
  pendingTasksCount: number;
  doneTasksCount: number;
  totalMoney: number;
  tasksArrivedData: { labels: string[]; data: number[] };
  newTaskersData: { labels: string[]; data: number[] };
  incomeData: { labels: string[]; data: number[] };
  completedTasksData: { labels: string[]; data: number[] };
}

export const fetchAnalyticsData = async (
  startDate: Date,
  endDate: Date,
  userProfile: User | null,
  selectedUser: string | null
): Promise<AnalyticsData> => {
  const isAdmin = userProfile?.role.includes('admin');
  const isHelper = userProfile?.role.includes('helper');

  let query = supabase
    .from('tasks')
    .select('*', { count: 'exact' })
    .gte('created_at', startDate.toISOString())
    .lte('created_at', endDate.toISOString());

  if (isHelper && userProfile?.id) {
    query = query
      .eq('assigned_user_id', userProfile.id)
      .eq('status', 'pending');
  } else if (isAdmin && selectedUser && selectedUser !== 'all') {
    query = query.eq('assigned_user_id', selectedUser);
  }

  const { data: fetchedTasks, count: tasksCount } = await query;

  // Fetch taskers count (only for admin)
  let taskersCount = 0;
  if (isAdmin) {
    const { count } = await supabase
      .from('taskers')
      .select('*', { count: 'exact' })
      .gte('created_at', startDate.toISOString())
      .lte('created_at', endDate.toISOString());
    taskersCount = count || 0;
  }

  // Fetch pending tasks count
  const pendingQuery = query.eq('status', 'pending');
  const { count: pendingCount } = await pendingQuery;

  // Fetch done tasks count and data
  const doneQuery = supabase
    .from('tasks')
    .select('*', { count: 'exact' })
    .eq('status', 'complete')
    .gte('created_at', startDate.toISOString())
    .lte('created_at', endDate.toISOString());

  if (!isAdmin && userProfile?.id) {
    doneQuery.eq('assigned_user_id', userProfile.id);
  } else if (isAdmin && selectedUser && selectedUser !== 'all') {
    doneQuery.eq('assigned_user_id', selectedUser);
  }

  const { data: completedTasks, count: doneCount } = await doneQuery;

  // Calculate total money
  let totalMoney = 0;
  if (userProfile?.role.includes('admin')) {
    totalMoney = (fetchedTasks || []).reduce((sum, task) => sum + (task.price || 0), 0);
  } else if (userProfile?.role.includes('helper')) {
    totalMoney = (completedTasks || []).reduce((sum, task) => sum + (task.price || 0) * 0.15, 0);
  } else {
    totalMoney = (fetchedTasks || []).reduce((sum, task) => sum + (task.price || 0), 0);
  }

  // Process tasks arrived over time
  const tasksArrivedData = processTimeSeriesData(fetchedTasks || [], 'created_at', startDate, endDate);

  // Process new taskers data (only for admin)
  let newTaskersData: { labels: string[]; data: number[] } = { labels: [], data: [] };
  if (isAdmin) {
    const { data: newTaskers } = await supabase
      .from('taskers')
      .select('created_at')
      .gte('created_at', startDate.toISOString())
      .lte('created_at', endDate.toISOString());

    newTaskersData = processTimeSeriesData<{ created_at: string }>(
      newTaskers || [],
      'created_at',
      startDate,
      endDate
    );
  }

  // Process income over time
  const incomeData = processTimeSeriesData(
    userProfile?.role.includes('helper') ? completedTasks || [] : fetchedTasks || [],
    'created_at',
    startDate,
    endDate,
    'price',
    userProfile?.role.includes('helper') ? 0.15 : 1
  );

  // Process completed tasks over time
  const completedTasksData = processTimeSeriesData(completedTasks || [], 'created_at', startDate, endDate);

  return {
    tasksCount: tasksCount || 0,
    taskersCount,
    pendingTasksCount: pendingCount || 0,
    doneTasksCount: doneCount || 0,
    totalMoney,
    tasksArrivedData,
    newTaskersData,
    incomeData,
    completedTasksData,
  };
};

const processTimeSeriesData = <T extends Record<string, unknown>>(
  data: T[],
  dateField: keyof T & string,
  startDate: Date,
  endDate: Date,
  valueField?: keyof T & string,
  percentage?: number
): { labels: string[]; data: number[] } => {
  const timeSeriesData: Record<string, number> = {};
  
  // Initialize all dates in the range with 0
  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    const dateString = d.toISOString().split('T')[0];
    timeSeriesData[dateString] = 0;
  }

  // Process the actual data
  data.forEach(item => {
    const date = new Date(item[dateField] as string).toISOString().split('T')[0];
    const value = valueField ? Number(item[valueField] as string | number) || 0 : 1;
    const adjustedValue = percentage ? value * percentage : value;
    timeSeriesData[date] = (timeSeriesData[date] || 0) + adjustedValue;
  });

  const sortedDates = Object.keys(timeSeriesData).sort();
  const sortedData = sortedDates.map(date => timeSeriesData[date]);

  return { labels: sortedDates, data: sortedData };
};